import React, { useCallback, useEffect, useState } from 'react';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { TeamUiModel } from '../../../shared/ui-models/team.ui-model';
import { AccessControlFilterConfigModel } from '../../../shared/models/team.model';
import {
  createTeam,
  deleteTeam,
  getAccessControlConfig,
  getTeams,
  updateTeam
} from '../../../shared/services/data/team.data';
import Button, { ButtonType } from '../../../shared/components/core-ui/button/button.component';
import { Loader } from '../../../shared/components/core-ui/loader/loader.component';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { TeamFormModal, TeamFormValue } from './components/team-form-modal.component';

export const TeamsListContainer: React.FC = () => {
  const [teams, setTeams] = useState<TeamUiModel[]>([]);
  const [filterConfig, setFilterConfig] = useState<AccessControlFilterConfigModel>({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<TeamUiModel | undefined>();

  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([getTeams(), getAccessControlConfig()]).then(([teamsResult, configResult]) => {
      if (!isErrorInfo(teamsResult)) setTeams(teamsResult);
      if (!isErrorInfo(configResult)) setFilterConfig(configResult);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = (value: TeamFormValue) => {
    const isEdit = Boolean(editing);
    const request = isEdit && editing ? updateTeam(editing.id, value) : createTeam(value);

    const promise = request.then((response: unknown) => {
      if (isErrorInfo(response as ErrorInfo)) throw response;
      setShowForm(false);
      setEditing(undefined);
      return load();
    });

    birdToast.promise(promise, {
      loadingMessage: isEdit ? 'Saving team...' : 'Creating team...',
      successMessage: isEdit ? 'Team saved' : 'Team created',
      errorPermissionMessage: 'You do not have permission to change teams',
      errorFailedMessage: isEdit ? 'Failed to save team' : 'Failed to create team'
    });
  };

  const handleDelete = (team: TeamUiModel) => {
    const promise = deleteTeam(team.id).then((response: boolean | ErrorInfo) => {
      if (isErrorInfo(response)) throw response;
      return load();
    });

    birdToast.promise(promise, {
      loadingMessage: 'Archiving team...',
      successMessage: 'Team archived',
      errorPermissionMessage: 'You do not have permission to archive a team',
      errorFailedMessage: 'Failed to archive team'
    });
  };

  return (
    <div className="flex h-full flex-col items-stretch md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[3rem]">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">Teams &amp; access</h1>
        <span className="ml-auto">
          <Button
            type={ButtonType.primary}
            onClick={() => {
              setEditing(undefined);
              setShowForm(true);
            }}
          >
            Create team
          </Button>
        </span>
      </div>

      <div className="rounded bg-white p-4 shadow-sm">
        {loading ? (
          <Loader />
        ) : teams.length === 0 ? (
          <p className="py-8 text-center text-gray-500">No teams yet.</p>
        ) : (
          teams.map((team) => {
            const leadGrant = team.grants.find((grant) => grant.module === 'leads');
            return (
              <div
                key={team.id}
                className="flex flex-wrap items-center gap-3 border-b border-gray-100 py-3"
              >
                <span className="font-medium">{team.name}</span>
                {team.isArchived ? (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                    archived
                  </span>
                ) : null}

                {!leadGrant ? (
                  <span className="text-xs text-gray-400">no lead grant</span>
                ) : leadGrant.isUnconditional ? (
                  // The most permissive thing a team can hold, and it renders
                  // as an empty row unless it is called out.
                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
                    all leads
                  </span>
                ) : (
                  leadGrant.filters.map((filter) => (
                    <span
                      key={filter.key}
                      className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700"
                    >
                      {filter.key}: {filter.values.join(', ')}
                    </span>
                  ))
                )}

                <span className="ml-auto flex gap-2">
                  <button
                    type="button"
                    className="text-sm text-blue-600"
                    onClick={() => {
                      setEditing(team);
                      setShowForm(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="text-sm text-red-600"
                    onClick={() => handleDelete(team)}
                  >
                    Archive
                  </button>
                </span>
              </div>
            );
          })
        )}
      </div>

      <TeamFormModal
        show={showForm}
        team={editing}
        filterConfig={filterConfig}
        onClose={() => {
          setShowForm(false);
          setEditing(undefined);
        }}
        onSubmit={handleSubmit}
      />
    </div>
  );
};
